import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  Button,
  TextField,
  Typography,
  CircularProgress,
  LinearProgress,
  Chip,
  FormControlLabel,
  Checkbox,
  Box,
  List,
  ListItem,
  ListItemText,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";

import axios from "axios";
import envConfig from "../../../bin/env.config";
import { useSelector } from "react-redux";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => ({
  section: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  fieldLast: {
    marginBottom: 0,
  },
  mappedInfo: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  stepList: {
    maxHeight: 160,
    overflow: "auto",
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(1),
  },
  alertBox: {
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    fontSize: "0.875rem",
  },
  alertError: {
    backgroundColor: "#fdecea",
    color: "#611a15",
  },
  alertWarning: {
    backgroundColor: "#fff4e5",
    color: "#663c00",
  },
}));

const STATUS_COLORS = {
  matched: "#2196f3",
  not_found: "#ff9800",
  error: "#f44336",
};

function parseLlmMeta(raw) {
  if (!raw) {
    return { product_name: "", size: "", brand: "", key_words: "" };
  }
  const meta = typeof raw === "string" ? JSON.parse(raw) : raw;
  const keywords = Array.isArray(meta.key_words)
    ? meta.key_words.join(", ")
    : (meta.key_words || "");
  return {
    product_name: meta.product_name || "",
    size: meta.size || "",
    brand: meta.brand || "",
    key_words: keywords,
  };
}

function buildLlmMetaFromForm(metaForm, hasMeta) {
  if (!hasMeta) return null;
  const keyWords = (metaForm.key_words || "")
    .split(/[,;|]/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  return {
    product_name: metaForm.product_name || "",
    size: metaForm.size?.trim() || null,
    brand: metaForm.brand?.trim() || null,
    key_words: keyWords,
  };
}

export default function DialogLlmSettings({ open, onClose, product, project_id, reloadData }) {
  const classes = useStyles();
  const token = useSelector((state) => state.session.token);
  const apiBase = `${envConfig.API_ROOT}${envConfig.API_VERSION}/company-report-tool`;

  const [numberPmgNeed, setNumberPmgNeed] = useState(1);
  const [hasMeta, setHasMeta] = useState(false);
  const [metaForm, setMetaForm] = useState({
    product_name: "",
    size: "",
    brand: "",
    key_words: "",
  });
  const [forceReextract, setForceReextract] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [projectJobRunning, setProjectJobRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [error, setError] = useState(null);

  const mappedCount = product?.products?.length ?? 0;

  const fetchProjectJobStatus = useCallback(async () => {
    if (!project_id) return;
    try {
      const res = await axios.get(`${apiBase}/auto-map/status`, {
        params: { project_id },
        headers: { Authorization: JSON.stringify(token) },
      });
      setProjectJobRunning(res.data?.data?.status === "running");
    } catch (err) {
      console.error("Failed to fetch auto-map status:", err);
    }
  }, [apiBase, project_id, token]);

  useEffect(() => {
    if (open && product) {
      setNumberPmgNeed(product.number_pmg_need ?? 1);
      const hasExistingMeta = Boolean(product.llm_meta);
      setHasMeta(hasExistingMeta);
      setMetaForm(parseLlmMeta(product.llm_meta));
      setForceReextract(false);
      setRunResult(null);
      setError(null);
      fetchProjectJobStatus();
    }
  }, [product, open, fetchProjectJobStatus]);

  const handleMetaChange = (event) => {
    const { name, value } = event.target;
    setMetaForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearMeta = () => {
    setHasMeta(false);
    setMetaForm({ product_name: "", size: "", brand: "", key_words: "" });
  };

  const buildPayload = () => ({
    number_pmg_need: Number(numberPmgNeed) || 1,
    llm_meta: buildLlmMetaFromForm(metaForm, hasMeta),
  });

  const onSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = buildPayload();
      await axios.post(
        `${apiBase}/update-product-llm-settings`,
        { id: product.id, project_id, ...payload },
        { headers: { Authorization: JSON.stringify(token) } }
      );
      reloadData();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const onRunLlm = async () => {
    setRunning(true);
    setRunResult(null);
    setError(null);
    try {
      const payload = buildPayload();
      const res = await axios.post(
        `${apiBase}/auto-map/run-product`,
        {
          product_id: product.id,
          project_id,
          force_reextract_meta: forceReextract,
          ...payload,
        },
        { headers: { Authorization: JSON.stringify(token) } }
      );
      const result = res.data?.data;
      setRunResult(result);
      if (result?.llm_meta) {
        setHasMeta(true);
        setMetaForm(parseLlmMeta(result.llm_meta));
      }
      if (result?.status === "matched") {
        reloadData();
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "LLM auto-map failed");
    } finally {
      setRunning(false);
    }
  };

  const busy = saving || running;
  const runDisabled = busy || projectJobRunning;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      TransitionComponent={Transition}
      aria-labelledby="llm-settings-dialog"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="llm-settings-dialog">
        LLM Settings — {product?.name}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Typography className={`${classes.alertBox} ${classes.alertError}`}>
            {error}
          </Typography>
        )}
        {projectJobRunning && (
          <Typography className={`${classes.alertBox} ${classes.alertWarning}`}>
            Project auto-map job is running. Stop it before running on a single product.
          </Typography>
        )}

        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>PMG target</Typography>
          <Typography className={classes.mappedInfo}>
            Mapped: {mappedCount} / {numberPmgNeed}
            {product?.llm_attempts > 0 && ` · Failed attempts: ${product.llm_attempts}`}
          </Typography>
          <TextField
            label="Number of PMGs needed"
            type="number"
            inputProps={{ min: 1, step: 1 }}
            fullWidth
            className={classes.fieldLast}
            value={numberPmgNeed}
            onChange={(e) => setNumberPmgNeed(e.target.value)}
            disabled={busy}
          />
        </div>

        <div className={classes.section}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
              LLM meta
            </Typography>
            {hasMeta && (
              <Button size="small" onClick={handleClearMeta} disabled={busy}>
                Clear
              </Button>
            )}
          </Box>
          {!hasMeta && (
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
              No LLM meta saved. Run LLM to auto-extract, or fill fields below.
            </Typography>
          )}
          <TextField
            label="Product name"
            name="product_name"
            fullWidth
            className={classes.field}
            value={metaForm.product_name}
            onChange={(e) => {
              setHasMeta(true);
              handleMetaChange(e);
            }}
            disabled={busy}
          />
          <TextField
            label="Size"
            name="size"
            fullWidth
            className={classes.field}
            value={metaForm.size}
            onChange={(e) => {
              setHasMeta(true);
              handleMetaChange(e);
            }}
            disabled={busy}
            placeholder="e.g. 355ml, 12oz"
          />
          <TextField
            label="Brand"
            name="brand"
            fullWidth
            className={classes.field}
            value={metaForm.brand}
            onChange={(e) => {
              setHasMeta(true);
              handleMetaChange(e);
            }}
            disabled={busy}
          />
          <TextField
            label="Key words"
            name="key_words"
            fullWidth
            multiline
            rows={2}
            className={classes.fieldLast}
            value={metaForm.key_words}
            onChange={(e) => {
              setHasMeta(true);
              handleMetaChange(e);
            }}
            disabled={busy}
            placeholder="Comma-separated keywords"
          />
        </div>

        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>Run LLM auto-map</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={forceReextract}
                onChange={(e) => setForceReextract(e.target.checked)}
                disabled={busy}
                color="primary"
              />
            }
            label="Re-extract meta from LLM"
          />
          {running && <LinearProgress style={{ marginBottom: 8 }} />}
          <Button
            color="primary"
            variant="contained"
            fullWidth
            startIcon={running ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
            onClick={onRunLlm}
            disabled={runDisabled}
          >
            {running ? "Running LLM…" : "Run LLM Auto-Map"}
          </Button>

          {runResult && (
            <Box mt={2}>
              <Chip
                label={runResult.status?.toUpperCase()}
                size="small"
                style={{
                  backgroundColor: STATUS_COLORS[runResult.status] || "#9e9e9e",
                  color: "#fff",
                  fontWeight: 600,
                  marginBottom: 8,
                }}
              />
              {runResult.pmg_id && (
                <Typography variant="body2" color="textSecondary">
                  Matched PMG ID: {runResult.pmg_id}
                </Typography>
              )}
              {runResult.message && (
                <Typography variant="body2" color="error">
                  {runResult.message}
                </Typography>
              )}
              {runResult.steps?.length > 0 && (
                <List dense className={classes.stepList}>
                  {runResult.steps.map((step, idx) => (
                    <ListItem key={idx} divider>
                      <ListItemText
                        primary={`${step.step} — ${step.status}`}
                        secondary={
                          step.detail
                            ? typeof step.detail === "string"
                              ? step.detail
                              : JSON.stringify(step.detail)
                            : null
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={onSave}
          disabled={busy}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? "Saving…" : "Save settings"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
